import { useCallback } from "react";
import { keyBy } from "lodash";
import { useLazyQuery } from "@apollo/client";
import { getTeamAndMembersAndChildTeamsQuery } from "src/queries/group.graphql";
import { getMissingTeamsForDiff as getMissingTeamsForDiffQuery } from "src/pages/TeamDetailPage/team.graphql";
import { getMissingMembersForDiffQuery } from "src/allocation/graphql/members.graphql";
import { MAX_ITEMS_PER_PAGE } from "src/components/OrgChart/consts";

export const ORG_CHART_STATE_KEYS = {
  DIFF_BASELINE: "baseline",
  DIFF_NEXT: "next",
  NORMAL: "normal",
};
export const DIFF_STATES = [
  ORG_CHART_STATE_KEYS.DIFF_BASELINE,
  ORG_CHART_STATE_KEYS.DIFF_NEXT,
];

export const getQueryOptions = (pitDate, stateKey) => {
  let QUERY_OPTIONS = {
    errorPolicy: "all",
    fetchPolicy: "no-cache",
  };

  if (DIFF_STATES.includes(stateKey)) {
    QUERY_OPTIONS = {
      ...QUERY_OPTIONS,
      context: { headers: { "x-pit-date": pitDate } },
    };
  }
  return QUERY_OPTIONS;
};

const getCacheBuster = () => {
  return { _CACHE_BUSTER: Math.random() };
};

const shouldExecuteQuery = (stateKey, isShowingChanges) => {
  const isFetchingNormalData = stateKey === ORG_CHART_STATE_KEYS.NORMAL;
  const isFetchingDiffData = !isFetchingNormalData;

  if (!isShowingChanges && isFetchingNormalData) {
    return true;
  }
  if (isShowingChanges && isFetchingDiffData) {
    return true;
  }

  return false;
};

const useGetTeamsForOrgChart = ({
  pitDate,
  stateKey = ORG_CHART_STATE_KEYS.NORMAL,
  state,
}) => {
  const { showChanges } = state.viewOptions;

  const [getTeamAndMembersAndChildTeams] = useLazyQuery(
    getTeamAndMembersAndChildTeamsQuery
  );
  const [getMissingTeamsForDiff] = useLazyQuery(getMissingTeamsForDiffQuery);
  const [getMissingMembersForDiff] = useLazyQuery(
    getMissingMembersForDiffQuery
  );

  const loadMissingTeamsForDiff = useCallback(
    async (groupIds) => {
      const result = await getMissingTeamsForDiff({
        variables: {
          groupIds,
          ...getCacheBuster(pitDate, stateKey),
        },
        ...getQueryOptions(pitDate, stateKey),
      });

      return result?.data?.getGroups;
    },
    [getMissingTeamsForDiff, pitDate, stateKey]
  );

  const loadMissingMembersForDiff = useCallback(
    async (ids) => {
      const result = await getMissingMembersForDiff({
        variables: {
          ids,
          ...getCacheBuster(pitDate, stateKey),
        },
        ...getQueryOptions(pitDate, stateKey),
      });

      return result?.data?.people;
    },
    [getMissingMembersForDiff, pitDate, stateKey]
  );

  const loadSpecificTeamDataById = useCallback(
    async (
      teamId,
      pagination = {
        fetchMembers: true,
        memberOffset: 0,
        fetchTeams: true,
        teamOffset: 0,
      }
    ) => {
      if (!shouldExecuteQuery(stateKey, showChanges)) {
        return null;
      }

      const result = await getTeamAndMembersAndChildTeams({
        variables: {
          groupId: teamId,
          includeMembers: pagination.fetchMembers,
          membersLimit: MAX_ITEMS_PER_PAGE,
          membersOffset: pagination.memberOffset,
          includeTeams: pagination.fetchTeams,
          teamsLimit: MAX_ITEMS_PER_PAGE,
          teamsOffset: pagination.teamOffset,
          ...getCacheBuster(pitDate, stateKey),
        },
        ...getQueryOptions(pitDate, stateKey),
      });

      const viewTeam = result?.data?.team;
      if (!viewTeam) {
        return { teams: {}, members: {} };
      }

      const members = viewTeam.members ?? [];
      const childTeams = viewTeam.childTeams ?? [];

      return {
        teams: keyBy([...childTeams, viewTeam], "id"),
        members: keyBy(members, "aggregateId"),
      };
    },
    [stateKey, showChanges, getTeamAndMembersAndChildTeams, pitDate]
  );

  return {
    loadSpecificTeamDataById,
    loadMissingTeamsForDiff,
    loadMissingMembersForDiff,
  };
};

export default useGetTeamsForOrgChart;
